import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import {
  Download,
  Calendar,
  Folder,
  FileText,
  CheckCircle2,
  Search,
  Settings,
  Trash2,
  Info,
} from "lucide-react";
import { useExports } from "../../lib/exports/useExports";
import { redownloadExport } from "../../lib/exports";
import type {
  ExportFormat,
  ExportRecord,
  ExportRequest,
} from "../../lib/exports/types";
import { buildQuickBooksCsv, buildXeroCsv } from "../../lib/exports/csv";
import { useCategories } from "../../lib/categories/useCategories";
import { useExpenses } from "../../lib/expenses/useExpenses";
import { useInvoices } from "../../lib/invoices/useInvoices";
import { useTaxPolicies } from "../../lib/taxPolicies/useTaxPolicies";
import { trackEvent } from "../../lib/telemetry/analytics";
import { ScopePinnedBadge } from "../components/ScopePinnedBadge";
import { useAuth } from "../../lib/auth/useAuth";
import { useCapabilities } from "../../lib/permissions/useCapabilities";
import { useEffectiveScopeForTab } from "../../lib/scope";

const BYTE_FORMATTER = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 1,
});

// Persist the last-chosen export format so Power users don't have to click
// the toggle on every page load.
const FORMAT_STORAGE_KEY = "proofly.exports.lastFormat";

function readLastFormat(): ExportFormat {
  if (typeof window === "undefined") return "csv";
  try {
    const raw = window.localStorage.getItem(FORMAT_STORAGE_KEY);
    if (raw === "csv" || raw === "pdf" || raw === "zip") return raw;
  } catch {
    // Ignore quota / privacy-mode errors.
  }
  return "csv";
}

function writeLastFormat(format: ExportFormat): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(FORMAT_STORAGE_KEY, format);
  } catch {
    // Ignore — best effort.
  }
}

function formatBytes(bytes: number | undefined): string {
  if (bytes === undefined || bytes === null) return "—";
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${BYTE_FORMATTER.format(kb)} KB`;
  const mb = kb / 1024;
  return `${BYTE_FORMATTER.format(mb)} MB`;
}

function formatCreatedAt(iso: string): string {
  try {
    const dt = new Date(iso);
    return new Intl.DateTimeFormat("en-US", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(dt);
  } catch {
    return iso;
  }
}

function isoDaysAgo(n: number): string {
  const now = new Date();
  now.setDate(now.getDate() - n);
  return now.toISOString().slice(0, 10);
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

const FORMAT_LABELS: Record<ExportFormat, string> = {
  csv: "CSV Data",
  pdf: "PDF Summary",
  zip: "Audit Package",
};

export function Exports() {
  const [searchParams] = useSearchParams();
  const prefill = searchParams.get("prefill") === "true";

  const { records, create, remove } = useExports();
  const { categories } = useCategories();
  const { expenses } = useExpenses();
  const { invoices } = useInvoices();
  const { taxPolicies } = useTaxPolicies();
  const { user } = useAuth();
  const { can } = useCapabilities();
  const { scope, pinned } = useEffectiveScopeForTab("exports");
  // In `mine` scope creating an export requires `export:mine`; in
  // `company` scope it requires `export:company`. Accountants carry
  // both by default so this gate only bites employees (mine) and
  // auditors (company) with a narrowed override.
  const requiredExportCap = scope === "mine" ? "export:mine" : "export:company";
  const canCreate = can(requiredExportCap);
  const scopedUserId = scope === "mine" ? user?.id ?? null : null;

  const [dateFrom, setDateFrom] = useState<string>(() =>
    prefill ? isoDaysAgo(90) : isoDaysAgo(30),
  );
  const [dateTo, setDateTo] = useState<string>(() => todayIso());
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [format, setFormat] = useState<ExportFormat>(() => readLastFormat());
  const [csvDest, setCsvDest] = useState<"generic" | "quickbooks" | "xero">("generic");
  const [includeReceipts, setIncludeReceipts] = useState(false);
  const [includeNotes, setIncludeNotes] = useState(false);
  const [historySearch, setHistorySearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (prefill && createRef.current) {
      // Auto-focus the Create Export button when the page is opened via the
      // ExpensesHub "Export" shortcut so the user can hit Enter to submit.
      createRef.current.focus();
    }
  }, [prefill]);

  function toggleCategory(id: string): void {
    setSelectedCategoryIds((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id],
    );
  }

  function clearCategories(): void {
    setSelectedCategoryIds([]);
  }

  function pickFormat(next: ExportFormat): void {
    setFormat(next);
    writeLastFormat(next);
  }

  async function handleCreate(): Promise<void> {
    if (busy) return;
    if (!canCreate) return;
    setError(null);
    // For QB/Xero destinations: generate client-side and download directly
    if (format === "csv" && csvDest !== "generic") {
      setBusy(true);
      try {
        const req: ExportRequest = {
          format: "csv",
          dateFrom: dateFrom || null,
          dateTo: dateTo || null,
          categoryIds: selectedCategoryIds.length > 0 ? selectedCategoryIds : undefined,
          includeReceipts: false,
          includeNotes: false,
        };
        // Build sources — receipts not needed for QB/Xero
        const sources = {
          expenses,
          invoices,
          categories,
          taxPolicies: taxPolicies ?? [],
          receipts: [],
        };
        const builder = csvDest === "quickbooks" ? buildQuickBooksCsv : buildXeroCsv;
        const result = builder(sources, req);
        const destLabel = csvDest === "quickbooks" ? "quickbooks" : "xero";
        const now = new Date();
        const filename = `proofly_${destLabel}_${now.toISOString().slice(0, 10)}.csv`;
        const blob = new Blob([result.content], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 0);
        trackEvent("export_generated", { format: `csv_${destLabel}`, has_date_range: Boolean(req.dateFrom || req.dateTo), category_count: req.categoryIds?.length ?? 0 });
      } finally {
        setBusy(false);
      }
      return;
    }
    const req: ExportRequest = {
      format,
      dateFrom: dateFrom || null,
      dateTo: dateTo || null,
      categoryIds:
        selectedCategoryIds.length > 0 ? selectedCategoryIds : undefined,
      includeReceipts,
      includeNotes,
      userId: scopedUserId,
    };
    setBusy(true);
    try {
      await create(req);
      trackEvent("export_generated", {
        format: req.format,
        has_date_range: Boolean(req.dateFrom || req.dateTo),
        category_count: req.categoryIds?.length ?? 0,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  function handleRedownload(record: ExportRecord): void {
    try {
      redownloadExport(record);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  async function handleRemove(id: string): Promise<void> {
    const ok = window.confirm("Remove this export from history?");
    if (!ok) return;
    try {
      await remove(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  const filteredRecords = useMemo(() => {
    const needle = historySearch.trim().toLowerCase();
    if (!needle) return records;
    return records.filter((r) => r.filename.toLowerCase().includes(needle));
  }, [records, historySearch]);

  // Inline preview of what the resulting export will contain. Same filter
  // rules as the CSV builder — keeps the UX honest about "0 rows" before the
  // user clicks.
  const previewCounts = useMemo(() => {
    const from = dateFrom || null;
    const to = dateTo || null;
    const categoryFilter =
      selectedCategoryIds.length > 0 ? new Set(selectedCategoryIds) : null;

    const matchingExpenses = expenses.filter((e) => {
      if (from && e.occurredOn < from) return false;
      if (to && e.occurredOn > to) return false;
      if (scopedUserId && e.submittedBy !== scopedUserId) return false;
      if (categoryFilter) {
        if (!e.categoryId || !categoryFilter.has(e.categoryId)) return false;
      }
      return true;
    });
    const matchingInvoices = categoryFilter
      ? []
      : invoices.filter((i) => {
          if (from && i.issuedOn < from) return false;
          if (to && i.issuedOn > to) return false;
          if (scopedUserId && i.submittedBy !== scopedUserId) return false;
          return true;
        });
    return {
      expenses: matchingExpenses.length,
      invoices: matchingInvoices.length,
      total: matchingExpenses.length + matchingInvoices.length,
    };
  }, [expenses, invoices, dateFrom, dateTo, selectedCategoryIds, scopedUserId]);

  const createButtonLabel = busy
    ? "Generating…"
    : format === "pdf"
      ? "Create PDF Export"
      : "Create Export";

  const subtitle =
    format === "pdf"
      ? "Generate and download audit-ready PDF reports"
      : "Generate and download audit-ready CSV files";

  const createDisabled = busy || !canCreate;
  const createTitle = !canCreate
    ? `Your role doesn't have ${requiredExportCap}. Ask an admin to grant it.`
    : undefined;

  return (
    <div
      className="h-full flex flex-col gap-6 max-w-6xl mx-auto"
      data-scope={scope}
      data-testid="exports-root"
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-slate-900">Exports</h1>
            {pinned && <ScopePinnedBadge forcedScope={scope} />}
          </div>
          <p className="text-slate-500 mt-1">{subtitle}</p>
        </div>
        <button
          ref={createRef}
          type="button"
          data-testid="exports-create"
          data-scope={scope}
          data-can-create={canCreate ? "true" : "false"}
          onClick={() => {
            void handleCreate();
          }}
          disabled={createDisabled}
          title={createTitle}
          aria-disabled={createDisabled}
          className="flex items-center gap-2 px-4 py-2 bg-[#0070F2] hover:bg-[#005BCA] disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-md shadow-sm transition-colors text-sm"
        >
          <Download className="w-4 h-4" /> {createButtonLabel}
        </button>
      </div>

      {error && (
        <div
          data-testid="exports-error"
          role="alert"
          className="px-4 py-3 bg-red-50 border border-red-100 text-red-700 text-sm rounded-xl"
        >
          {error}
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-6 flex-1 min-h-0">
        {/* Left Col - Settings */}
        <div className="lg:w-1/3 flex flex-col gap-6">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 flex flex-col gap-4">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-2">
              <Settings className="w-5 h-5 text-slate-400" /> Export
              Configuration
            </h3>

            <div className="space-y-4">
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-slate-700">
                  Date Range
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <label className="flex flex-col gap-1 text-xs text-slate-600">
                    From
                    <input
                      type="date"
                      data-testid="exports-date-from"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#0070F2]/30"
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-xs text-slate-600">
                    To
                    <input
                      type="date"
                      data-testid="exports-date-to"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#0070F2]/30"
                    />
                  </label>
                </div>
                <p className="text-xs text-slate-500 flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> Leave blank to include all
                  dates
                </p>
              </div>

              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-slate-700">
                    Categories
                  </label>
                  {selectedCategoryIds.length > 0 && (
                    <button
                      type="button"
                      onClick={clearCategories}
                      className="text-xs text-slate-500 hover:text-slate-800"
                    >
                      Clear
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {selectedCategoryIds.length === 0 && (
                    <span
                      data-testid="exports-category-all"
                      className="px-3 py-1.5 bg-blue-50 text-blue-700 border border-blue-100 rounded-lg text-xs font-medium"
                    >
                      All Categories
                    </span>
                  )}
                  {categories.map((c) => {
                    const active = selectedCategoryIds.includes(c.id);
                    return (
                      <button
                        key={c.id}
                        type="button"
                        data-testid={`exports-category-${c.id}`}
                        aria-pressed={active}
                        onClick={() => toggleCategory(c.id)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer border transition-colors ${
                          active
                            ? "bg-[#0070F2] text-white border-[#0070F2]"
                            : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                        }`}
                      >
                        {c.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-slate-700">
                  Format
                </label>
                {/* Segmented control — Tailwind-only implementation, no
                    Radix dependency needed since there are only two live
                    options. ZIP remains disabled pending backend work. */}
                <div
                  role="radiogroup"
                  aria-label="Export format"
                  data-testid="exports-format-group"
                  className="grid grid-cols-3 gap-2"
                >
                  <button
                    type="button"
                    role="radio"
                    data-testid="exports-format-csv"
                    aria-checked={format === "csv"}
                    aria-pressed={format === "csv"}
                    onClick={() => pickFormat("csv")}
                    className={`text-left rounded-xl p-3 border-2 transition-colors ${
                      format === "csv"
                        ? "border-[#0070F2] bg-blue-50"
                        : "border-slate-200 bg-white hover:border-slate-300"
                    }`}
                  >
                    <div className="font-semibold text-slate-900 text-sm">
                      {FORMAT_LABELS.csv}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      Raw spreadsheet
                    </div>
                  </button>
                  <button
                    type="button"
                    role="radio"
                    data-testid="exports-format-pdf"
                    aria-checked={format === "pdf"}
                    aria-pressed={format === "pdf"}
                    onClick={() => pickFormat("pdf")}
                    className={`text-left rounded-xl p-3 border-2 transition-colors ${
                      format === "pdf"
                        ? "border-[#0070F2] bg-blue-50"
                        : "border-slate-200 bg-white hover:border-slate-300"
                    }`}
                  >
                    <div className="font-semibold text-slate-900 text-sm">
                      {FORMAT_LABELS.pdf}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      Audit-ready report
                    </div>
                  </button>
                  <button
                    type="button"
                    role="radio"
                    data-testid="exports-format-zip"
                    aria-checked={format === "zip"}
                    aria-pressed={format === "zip"}
                    onClick={() => pickFormat("zip")}
                    className={`text-left rounded-xl p-3 border-2 transition-colors ${
                      format === "zip"
                        ? "border-[#0070F2] bg-blue-50"
                        : "border-slate-200 bg-white hover:border-slate-300"
                    }`}
                  >
                    <div className="font-semibold text-slate-900 text-sm">
                      {FORMAT_LABELS.zip}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      CSV + PDF + raw receipts
                    </div>
                  </button>
                </div>
                {format === "csv" && (
                  <div className="flex flex-col gap-2 pt-3 border-t border-slate-100 mt-2">
                    <label className="text-sm font-medium text-slate-700">Destination</label>
                    <div className="flex gap-2 flex-wrap">
                      {(
                        [
                          { id: "generic", label: "Generic CSV" },
                          { id: "quickbooks", label: "QuickBooks" },
                          { id: "xero", label: "Xero" },
                        ] as const
                      ).map((d) => (
                        <button
                          key={d.id}
                          type="button"
                          onClick={() => setCsvDest(d.id)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                            csvDest === d.id
                              ? "bg-[#0070F2] text-white border-[#0070F2]"
                              : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                          }`}
                        >
                          {d.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-2 pt-2">
                <label className="text-sm font-medium text-slate-700">
                  Options
                </label>
                <div className="flex flex-col gap-2">
                  <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="exports-include-receipts"
                      checked={includeReceipts}
                      onChange={(e) => setIncludeReceipts(e.target.checked)}
                      className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    Include receipt references
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="exports-include-notes"
                      checked={includeNotes}
                      onChange={(e) => setIncludeNotes(e.target.checked)}
                      className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    Include business purpose
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-slate-50 rounded-2xl border border-slate-200 p-6">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-3">
              <Folder className="w-5 h-5 text-slate-400" /> Preview
            </h3>
            <div
              data-testid="exports-preview"
              className="text-sm text-slate-700 space-y-1"
            >
              <div className="flex justify-between">
                <span className="text-slate-500">Expenses in scope</span>
                <span className="font-semibold">{previewCounts.expenses}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Invoices in scope</span>
                <span className="font-semibold">{previewCounts.invoices}</span>
              </div>
              <div className="flex justify-between pt-2 border-t border-slate-200">
                <span className="text-slate-600 font-medium">Total rows</span>
                <span className="font-bold text-slate-900">
                  {previewCounts.total}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Col - History */}
        <div className="flex-1 bg-white rounded-2xl shadow-sm border border-slate-200 flex flex-col overflow-hidden min-h-0">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
            <h3 className="font-semibold text-slate-900">Export History</h3>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={historySearch}
                onChange={(e) => setHistorySearch(e.target.value)}
                placeholder="Search exports..."
                className="pl-9 pr-4 py-1.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 transition-shadow"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {filteredRecords.length === 0 ? (
              <div
                data-testid="exports-history-empty-state"
                className="h-full flex flex-col items-center justify-center gap-3 text-center p-12 text-slate-500"
              >
                <Info className="w-10 h-10 text-slate-300" />
                <div className="text-sm font-medium text-slate-700">
                  {records.length === 0
                    ? "No exports yet"
                    : "No exports match your search"}
                </div>
                <div className="text-xs text-slate-500">
                  {records.length === 0
                    ? "Configure the left panel and click Create Export."
                    : "Try a different search term."}
                </div>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {filteredRecords.map((record) => (
                  <div
                    key={record.id}
                    data-testid={`exports-history-row-${record.id}`}
                    className="p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center text-slate-500">
                        <FileText className="w-5 h-5 text-slate-500" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900 text-sm">
                          {record.filename}
                        </h4>
                        <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
                          <span>{formatCreatedAt(record.createdAt)}</span>
                          <span>•</span>
                          <span>{formatBytes(record.sizeBytes)}</span>
                          <span>•</span>
                          <span>
                            {record.rowCount} row
                            {record.rowCount === 1 ? "" : "s"}
                          </span>
                          <span>•</span>
                          <span className="flex items-center gap-1 text-green-600">
                            <CheckCircle2 className="w-3 h-3" /> Ready
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        data-testid={`exports-history-download-${record.id}`}
                        aria-label={`Download ${record.filename}`}
                        onClick={() => handleRedownload(record)}
                        className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      >
                        <Download className="w-5 h-5" />
                      </button>
                      <button
                        type="button"
                        data-testid={`exports-history-delete-${record.id}`}
                        aria-label={`Remove ${record.filename}`}
                        onClick={() => {
                          void handleRemove(record.id);
                        }}
                        className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
